import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { C } from '../../utils/styles';
import { useUsers } from '../../context/UsersContext';
import { useClients } from '../../context/ClientsContext';
import { useConfig } from '../../context/ConfigContext';
import api from '../../utils/api';

import DashboardFilterBar from './DashboardFilterBar';
import MetricsRow from './MetricsRow';
import PipelineFunnel from './PipelineFunnel';
import EnrollmentTrendChart from './EnrollmentTrendChart';
import CommissionSummary from './CommissionSummary';
import MessagesPanel from './MessagesPanel';
import FollowUpsCard from './FollowUpsCard';
import MetricBreakdownSheet from './MetricBreakdownSheet';
import LearningStatsRow from './LearningStatsRow';

import {
  filterClients,
  computeKpis,
  pipelineCounts,
  topClientsByFica,
  statesWithEmployeeCounts,
  amsWithClientCounts,
  amCommissionBreakdown,
  bucketFollowUps,
  buildTrendSnapshots,
} from '../../utils/dashboardData';

const SESSION_KEY = 'hc-dashboard-filters-v1';

function loadFilters() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) { /* ignore */ }
  return { amId: 'all', state: 'all', stage: 'all', timePeriod: 'all' };
}

function saveFilters(filters) {
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(filters)); } catch (e) { /* ignore */ }
}

function fmtMoney(n) {
  const v = Math.round(Number(n) || 0);
  if (Math.abs(v) >= 1000) return '$' + (v / 1000).toFixed(v >= 1_000_000 ? 2 : 1).replace(/\.0$/, '') + 'k';
  return '$' + v.toLocaleString('en-US');
}
function fmtNum(n) { return (Number(n) || 0).toLocaleString('en-US'); }

export default function InternalDashboard({ onOpenClient, onSwitchTab }) {
  const { currentUser, isSuperUser, users, loadUsers, getInitials } = useUsers();
  const { clients } = useClients();
  const { config } = useConfig();

  const isSuper = !!isSuperUser?.();
  const userId = currentUser?.id || currentUser?._id;
  const currentUserName = currentUser ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() : '';

  // Filters (session-persisted)
  const [filters, setFilters] = useState(() => {
    const initial = loadFilters();
    // For non-super users, AM filter is locked to themselves
    if (!isSuper && currentUser) {
      return { ...initial, amId: currentUser.id || currentUser._id };
    }
    return initial;
  });

  useEffect(() => { saveFilters(filters); }, [filters]);

  // Lock AM to current user for AMs even if filters change
  useEffect(() => {
    if (!isSuper && userId && filters.amId !== userId) {
      setFilters((f) => ({ ...f, amId: userId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper, userId]);

  // Load users (super user only — needed for AM filter, commission name resolution).
  // For AMs, we synthesize the current user into the commission lookup below.
  useEffect(() => {
    if (isSuper) loadUsers?.();
  }, [isSuper, loadUsers]);

  // Messages summary (polls every 30s)
  const [messagesSummary, setMessagesSummary] = useState(null);
  useEffect(() => {
    let mounted = true;
    const fetchSummary = async () => {
      try {
        const data = await api.dashboard.messagesSummary();
        if (mounted) setMessagesSummary(data);
      } catch (e) {
        if (mounted) setMessagesSummary({ myUnreadMessages: [], unansweredByAm: [] });
      }
    };
    fetchSummary();
    const t = setInterval(fetchSummary, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  // Apply filters
  const filteredClients = useMemo(
    () => filterClients(clients, filters, currentUser, isSuper),
    [clients, filters, currentUser, isSuper]
  );

  // For the pipeline funnel counts only, apply every filter EXCEPT `stage`.
  // Otherwise selecting one stage would zero-out every other card (because
  // `filteredClients` would only contain that stage's clients).
  const stageAgnosticClients = useMemo(
    () => filterClients(clients, { ...filters, stage: 'all' }, currentUser, isSuper),
    [clients, filters, currentUser, isSuper]
  );

  // Aggregations
  const kpis = useMemo(
    () => computeKpis(filteredClients, config?.revenueSettings),
    [filteredClients, config]
  );
  const counts = useMemo(() => pipelineCounts(stageAgnosticClients), [stageAgnosticClients]);
  const trendSnapshots = useMemo(() => buildTrendSnapshots(filteredClients), [filteredClients]);
  const followUpBuckets = useMemo(() => bucketFollowUps(filteredClients), [filteredClients]);
  const amBreakdown = useMemo(() => {
    // Ensure the current user is in the lookup so AM-only views can resolve their own name.
    const lookupUsers = (users && users.length > 0)
      ? users
      : (currentUser ? [currentUser] : []);
    return amCommissionBreakdown(filteredClients, lookupUsers, config?.revenueSettings);
  }, [filteredClients, users, currentUser, config]);

  // Filter dropdown options (always derived from full client set, not filtered, except respecting AM scoping)
  const baseScopedClients = useMemo(() => {
    if (isSuper) return clients;
    return (clients || []).filter((c) => c?.crm?.assignedTo === userId);
  }, [clients, isSuper, userId]);
  const stateOptions = useMemo(() => statesWithEmployeeCounts(baseScopedClients), [baseScopedClients]);
  const amOptions = useMemo(() => amsWithClientCounts(clients, users), [clients, users]);

  // Drill-down state
  const [breakdown, setBreakdown] = useState(null);

  const closeBreakdown = useCallback(() => setBreakdown(null), []);

  const openBreakdown = useCallback((metricKey) => {
    let title = '';
    let subtitle = '';
    let items = [];
    let formatter = (v) => v;

    const baseClients = filteredClients;
    if (metricKey === 'totalLives' || metricKey === 'totalClients') {
      title = 'Total lives by client';
      subtitle = 'Sorted by lives';
      formatter = (v) => `${fmtNum(v)} lives`;
      items = baseClients
        .map((c) => ({
          id: c.id, label: c?.company?.legalName || 'Unnamed',
          subline: c?.company?.address?.state || '',
          value: Number(c?.summary?.totalEmployees) || 0,
          client: c,
        }))
        .filter((x) => x.value > 0)
        .sort((a, b) => b.value - a.value);
    } else if (metricKey === 'eligible') {
      title = 'Eligible lives by client';
      subtitle = 'Sorted by eligible';
      formatter = (v) => `${fmtNum(v)} eligible`;
      items = baseClients
        .map((c) => ({
          id: c.id, label: c?.company?.legalName || 'Unnamed',
          subline: c?.company?.address?.state || '',
          value: Number(c?.summary?.eligible) || 0,
          client: c,
        }))
        .filter((x) => x.value > 0)
        .sort((a, b) => b.value - a.value);
    } else if (metricKey === 'annualFica') {
      title = 'Annual FICA savings by client';
      subtitle = 'Top contributors first';
      formatter = (v) => fmtMoney(v) + '/yr';
      items = topClientsByFica(baseClients, 30)
        .filter((c) => Number(c?.summary?.annualEmployerFica) > 0)
        .map((c) => ({
          id: c.id, label: c?.company?.legalName || 'Unnamed',
          subline: c?.company?.address?.state || '',
          value: Number(c?.summary?.annualEmployerFica) || 0,
          client: c,
        }));
    } else if (metricKey === 'enrolled') {
      title = 'Enrolled lives by client';
      subtitle = 'Sum of employees with enrolled status';
      formatter = (v) => `${fmtNum(v)} enrolled`;
      items = baseClients
        .map((c) => {
          const enrolled = (c?.employees || []).filter((e) => e?.enrollmentStatus === 'enrolled' || e?.enrollmentStatus === 'opted_in').length;
          return {
            id: c.id,
            label: c?.company?.legalName || 'Unnamed',
            subline: c?.company?.address?.state || '',
            value: enrolled,
            client: c,
          };
        })
        .filter((x) => x.value > 0)
        .sort((a, b) => b.value - a.value);
    } else if (metricKey === 'annualHcRevenue') {
      title = 'Annual HC revenue by client';
      subtitle = 'Active clients only';
      formatter = (v) => fmtMoney(v) + '/yr';
      items = baseClients
        .map((c) => ({
          id: c.id, label: c?.company?.legalName || 'Unnamed',
          subline: c?.company?.address?.state || '',
          value: Number(c?.summary?.annualHcRevenue) || 0,
          client: c,
        }))
        .filter((x) => x.value > 0)
        .sort((a, b) => b.value - a.value);
    } else if (metricKey === 'annualCommission' || metricKey === 'hcMargin') {
      // Per-AM breakdown
      title = metricKey === 'annualCommission' ? 'Annual commission by AM' : 'HC margin by AM';
      subtitle = `Total: ${fmtMoney(metricKey === 'annualCommission' ? kpis.annualCommission : kpis.hcMargin)}`;
      formatter = (v) => fmtMoney(v) + '/yr';
      const pct = metricKey === 'annualCommission' ? kpis.brokerPct : kpis.marginPct;
      items = amBreakdown
        .filter((a) => a.spread > 0)
        .map((a) => ({
          id: a.id, label: a.name,
          subline: `${a.count} client${a.count !== 1 ? 's' : ''} \u00b7 ${fmtMoney(a.spread)}/yr spread`,
          value: a.spread * (pct || 0),
          am: a,
        }));
    } else {
      title = metricKey;
      items = [];
    }

    setBreakdown({ key: metricKey, title, subtitle, items, formatter });
  }, [filteredClients, amBreakdown, kpis]);

  const handleClientItemClick = useCallback((item) => {
    if (item?.client && onOpenClient) {
      onOpenClient(item.client.id || item.client._id);
      setBreakdown(null);
    }
  }, [onOpenClient]);

  const handleClearFilters = useCallback(() => {
    const reset = isSuper
      ? { amId: 'all', state: 'all', stage: 'all', timePeriod: 'all' }
      : { amId: userId, state: 'all', stage: 'all', timePeriod: 'all' };
    setFilters(reset);
  }, [isSuper, userId]);

  const handleStageClick = useCallback((stageId) => {
    setFilters((f) => ({ ...f, stage: stageId || 'all' }));
  }, []);

  const handleClientClick = useCallback((c) => {
    if (onOpenClient) onOpenClient(c.id || c._id);
  }, [onOpenClient]);

  const handleViewAllClients = useCallback(() => {
    onSwitchTab && onSwitchTab('clients');
  }, [onSwitchTab]);

  const handleMessageClick = useCallback((m) => {
    if (m?.clientId && onOpenClient) onOpenClient(m.clientId);
  }, [onOpenClient]);

  return (
    <div data-testid="internal-dashboard" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: C.text, letterSpacing: '-0.02em' }}>
            {isSuper ? 'Command Center' : `Welcome back, ${currentUser?.firstName || ''}`}
          </h1>
          <p style={{ fontSize: 13, color: C.textMid, margin: '4px 0 0' }}>
            {isSuper
              ? 'Portfolio metrics, pipeline status, enrollment, and accountability in one view.'
              : 'Your book at a glance: clients, pipeline, follow-ups, and messages.'}
          </p>
        </div>
        <div style={{ fontSize: 11, color: C.textLight }}>
          Logged in as <strong style={{ color: C.text }}>{currentUserName}</strong>
          {isSuper ? ' (Super User)' : ' (Account Manager)'}
        </div>
      </div>

      <DashboardFilterBar
        filters={filters}
        onChange={setFilters}
        onClear={handleClearFilters}
        amOptions={amOptions}
        stateOptions={stateOptions}
        isSuperUser={isSuper}
        currentAmName={currentUserName}
      />

      <MetricsRow kpis={kpis} onCardClick={openBreakdown} />

      <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 16 }}>
        <PipelineFunnel
          counts={counts}
          clients={filteredClients}
          selectedStage={filters.stage}
          onStageClick={handleStageClick}
          onClientClick={handleClientClick}
          onViewAllClick={handleViewAllClients}
        />
        <EnrollmentTrendChart snapshots={trendSnapshots} kpis={kpis} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 16 }}>
        <CommissionSummary
          kpis={kpis}
          byAm={amBreakdown}
          revenueSettings={config?.revenueSettings}
          isSuperUser={isSuper}
          currentUserId={userId}
          onAmClick={(a) => {
            // Filter to that AM (super user only)
            if (isSuper) setFilters((f) => ({ ...f, amId: a.id }));
          }}
        />
        <MessagesPanel
          summary={messagesSummary}
          isSuperUser={isSuper}
          onMessageClick={handleMessageClick}
          onAmClick={(am) => {
            if (am?.clients?.[0]?.clientId && onOpenClient) {
              onOpenClient(am.clients[0].clientId);
            }
          }}
        />
      </div>

      <FollowUpsCard
        buckets={followUpBuckets}
        onViewAllClick={handleViewAllClients}
        onItemClick={(it) => onOpenClient && onOpenClient(it.clientId)}
      />

      <LearningStatsRow isSuperUser={isSuper} />

      <MetricBreakdownSheet
        open={!!breakdown}
        title={breakdown?.title}
        subtitle={breakdown?.subtitle}
        items={breakdown?.items || []}
        valueFormatter={breakdown?.formatter}
        onClose={closeBreakdown}
        onItemClick={breakdown?.items?.[0]?.client ? handleClientItemClick : undefined}
      />
    </div>
  );
}
